import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const barStep = 27;
const barPadding = 3 / barStep;
const color = d3.scaleOrdinal([true, false], ['steelblue', '#aaa']);
const duration = 750;
const marginTop = 30;
const marginRight = 30;
const marginBottom = 0;
const marginLeft = 140;
const width = 950;

const x = d3.scaleLinear().range([marginLeft, width - marginRight]);

const HorizontalBarsAnimatedChart = ({ data }) => {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const validData = data;

  const root = d3
    .hierarchy(validData)
    .sum((d) => d.value)
    .sort((a, b) => b.value - a.value)
    .eachAfter((d) => (d.index = d.parent ? (d.parent.index = d.parent.index + 1 || 0) : 0));

  function findMaxChildrenCount(data) {
    if (!data.children || data.children.length === 0) {
      return 0; // No children, so the count is 0
    }

    let maxCount = data.children.length;

    for (const child of data.children) {
      const childCount = findMaxChildrenCount(child);
      if (childCount > maxCount) {
        maxCount = childCount;
      }
    }

    return maxCount;
  }

  const height = findMaxChildrenCount(root) * barStep + marginTop + marginBottom;

  const xAxis = (g) =>
    g
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${marginTop})`)
      .call(d3.axisTop(x).ticks(width / 80, 's'))
      .call((g) => (g.selection ? g.selection() : g).select('.domain').remove());
  //
  const bar = (svg, down, d, selector) => {
    const g = svg
      .insert('g', selector)
      .attr('class', 'enter')
      .attr('transform', `translate(0,${marginTop + barStep * barPadding})`)
      .attr('text-anchor', 'end')
      .style('font', '10px sans-serif');

    const bar = g
      .selectAll('g')
      .data(d.children)
      .join('g')
      .attr('cursor', (d) => (!d.children ? null : 'pointer'))
      .on('click', (event, d) => down(svg, d));

    bar
      .append('text')
      .attr('x', marginLeft - 6)
      .attr('y', (barStep * (1 - barPadding)) / 2)
      .attr('dy', '.35em')
      .text((d) => d.data.name);

    bar
      .append('rect')
      .attr('x', x(0))
      .attr('width', (d) => x(d.value) - x(0))
      .attr('height', barStep * (1 - barPadding));

    return g;
  };

  const down = (svg, d) => {
    if (!d.children || d3.active(svg.node())) return;

    svg.select('.background').datum(d);

    const transition1 = svg.transition().duration(duration);
    const transition2 = transition1.transition();

    const exit = svg.selectAll('.enter').attr('class', 'exit');

    exit.selectAll('rect').attr('fill-opacity', (p) => (p === d ? 0 : null));

    exit.transition(transition1).attr('fill-opacity', 0).remove();

    const enter = bar(svg, down, d, '.y-axis').attr('fill-opacity', 0);

    enter.transition(transition1).attr('fill-opacity', 1);

    enter.selectAll('g').attr('transform', stack(d.index)).transition(transition1).attr('transform', stagger());

    x.domain([0, d3.max(d.children, (d) => d.value)]);

    svg.selectAll('.x-axis').transition(transition2).call(xAxis);

    enter
      .selectAll('g')
      .transition(transition2)
      .attr('transform', (d, i) => `translate(0,${barStep * i})`);

    enter
      .selectAll('rect')
      .attr('fill', color(true))
      .attr('fill-opacity', 1)
      .transition(transition2)
      .attr('fill', (d) => color(!!d.children))
      .attr('width', (d) => x(d.value) - x(0));
  };

  const up = (svg, d) => {
    if (!d.parent || !svg.selectAll('.exit').empty()) return;

    svg.select('.background').datum(d.parent);

    const transition1 = svg.transition().duration(duration);
    const transition2 = transition1.transition();

    const exit = svg.selectAll('.enter').attr('class', 'exit');

    x.domain([0, d3.max(d.parent.children, (d) => d.value)]);

    svg.selectAll('.x-axis').transition(transition1).call(xAxis);

    exit.selectAll('g').transition(transition1).attr('transform', stagger());

    exit.selectAll('g').transition(transition2).attr('transform', stack(d.index));

    exit
      .selectAll('rect')
      .transition(transition1)
      .attr('width', (d) => x(d.value) - x(0))
      .attr('fill', color(true));

    exit.transition(transition2).attr('fill-opacity', 0).remove();

    const enter = bar(svg, down, d.parent, '.exit').attr('fill-opacity', 0);

    enter.selectAll('g').attr('transform', (d, i) => `translate(0,${barStep * i})`);

    enter.transition(transition2).attr('fill-opacity', 1);

    enter
      .selectAll('rect')
      .attr('fill', (d) => color(!!d.children))
      .attr('fill-opacity', (p) => (p === d ? 0 : null))
      .transition(transition2)
      .attr('width', (d) => x(d.value) - x(0))
      .on('end', function (p) {
        d3.select(this).attr('fill-opacity', 1);
      });
  };
  const yAxis = (g) =>
    g
      .attr('class', 'y-axis')
      .attr('transform', `translate(${marginLeft + 0.5},0)`)
      .call((g) =>
        g
          .append('line')
          .attr('stroke', 'currentColor')
          .attr('y1', marginTop)
          .attr('y2', height - marginBottom),
      );

  const stagger = () => {
    let value = 0;
    return (d, i) => {
      const t = `translate(${x(value) - x(0)},${barStep * i})`;
      value += d.value;
      return t;
    };
  };

  const stack = (i) => {
    let value = 0;
    return (d) => {
      const t = `translate(${x(value) - x(0)},${barStep * i})`;
      value += d.value;
      return t;
    };
  };

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    svg
      .attr('viewBox', [0, 0, width, height])
      .attr('width', width)
      .attr('height', height)
      .attr('style', 'max-width: 100%; height: auto;');

    x.domain([0, root.value]);

    svg
      .append('rect')
      .attr('class', 'background')
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .attr('width', width)
      .attr('height', height)
      .attr('cursor', 'pointer')
      .on('click', (event, d) => up(svg, d));

    svg.append('g').call(xAxis);

    svg.append('g').call(yAxis);

    down(svg, root);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validData]);

  //

  return (
    <div ref={wrapperRef}>
      <svg ref={svgRef} />
    </div>
  );
};

export default HorizontalBarsAnimatedChart;
